/* AppraisalComponents/BedPointMaterialsBar.jsx
 * Phase C · C4e Ridge/Barge extraction (2026-02c).
 * Extracted verbatim from AppraisalComponentsTab.js:4782-4971.
 * Bed & Point materials bar shown under both Ridge and Barge sections
 * (each with its own coverage defaults), when ≥1 bed-and-point or
 * pointing-only line exists in that section.
 */
import React, { useState, useEffect } from 'react';

export function BedPointMaterialsBar({ section, lines, miscRates, defaults, onCommit }) {
  const candidates = (lines || []).filter(l => {
    const notes = String(l && l.notes || '');
    return !/^\[AUTO:(bedding_mortar|pointing_compound)_/i.test(notes);
  });
  const isBedPoint  = (l) => /(bed\s*[&andn]+\s*point|concrete barging|cement.*barging)/i.test(l.type_name || '');
  const isPointOnly = (l) => /^point\b/i.test(l.type_name || '') && !isBedPoint(l);
  const bpLines = candidates.filter(isBedPoint);
  const poLines = candidates.filter(isPointOnly);

  const safeMeters = (arr) => arr.reduce((acc, l) => {
    const q = Number(l.quantity) || 0;
    const len = Number(l.length) || 0;
    return acc + (len > 0 ? q * len : q);
  }, 0);
  const bedPointM  = safeMeters(bpLines);
  const pointOnlyM = safeMeters(poLines);

  // Read saved state from existing auto-lines (notes tag carries config).
  const mortarLine   = (lines || []).find(l => (l.notes || '').startsWith(`[AUTO:bedding_mortar_${section.toLowerCase()}]`));
  const pointingLine = (lines || []).find(l => (l.notes || '').startsWith(`[AUTO:pointing_compound_${section.toLowerCase()}]`));
  const parseTag = (notes, key) => {
    const m = (notes || '').match(new RegExp(`${key}=([^\\s·]+)`));
    return m ? m[1] : null;
  };
  const savedBuf   = Number((parseTag(mortarLine?.notes, 'buf') || parseTag(pointingLine?.notes, 'buf') || '10').replace('%','')) || 10;
  const savedMcov  = Number(parseTag(mortarLine?.notes, 'cov')) || defaults.mortar_cov;
  const savedPcov  = Number(parseTag(pointingLine?.notes, 'cov')) || defaults.pointing_cov;
  const savedCap   = Number((parseTag(mortarLine?.notes, 'cap') || '25').replace('mL','')) || 25;
  const savedMortarQty   = Number(mortarLine?.quantity) || 0;
  const savedPointingQty = Number(pointingLine?.quantity) || 0;
  const savedSupplier    = mortarLine?.supplier || 'Bunnings';
  const savedProductName = pointingLine?.type_name || 'PointWork 10L';

  // Hooks declared above any conditional return to keep call order stable.
  const [buf, setBuf]         = React.useState(String(savedBuf));
  const [mCov, setMCov]       = React.useState(String(savedMcov));
  const [pCov, setPCov]       = React.useState(String(savedPcov));
  const [capML, setCapML]     = React.useState(String(savedCap));
  const [supplier, setSupp]   = React.useState(savedSupplier);
  const [productName, setProd] = React.useState(savedProductName);
  React.useEffect(() => { setBuf(String(savedBuf)); },     [savedBuf]);
  React.useEffect(() => { setMCov(String(savedMcov)); },   [savedMcov]);
  React.useEffect(() => { setPCov(String(savedPcov)); },   [savedPcov]);
  React.useEffect(() => { setCapML(String(savedCap)); },   [savedCap]);
  React.useEffect(() => { setSupp(savedSupplier); },        [savedSupplier]);
  React.useEffect(() => { setProd(savedProductName); },     [savedProductName]);

  const bufNum  = (Number(buf) || 0) / 100;
  const mCovNum = Math.max(0.01, Number(mCov) || 0.01);
  const pCovNum = Math.max(0.01, Number(pCov) || 0.01);
  const rawBags    = bedPointM / mCovNum;
  const rawBuckets = (bedPointM + pointOnlyM) / pCovNum;
  const totalBags    = Math.ceil(rawBags * (1 + bufNum));
  const totalBuckets = Math.ceil(rawBuckets * (1 + bufNum));
  const plasticizerMl = totalBags * 2 * (Number(capML) || 0);
  const plasticizerL  = plasticizerMl / 1000;

  if (bpLines.length === 0 && poLines.length === 0) return null;

  const pointingOptions = (miscRates || []).filter(r =>
    r.is_accessory && /pointing|flexipoint|pointwork/i.test(r.type_name || '')
  );
  const mortarSuppliers = ['Bunnings', 'Mitre 10', 'Hardware Supplier'];

  const commit = () => {
    onCommit({
      mortar_qty:    totalBags,
      pointing_qty:  totalBuckets,
      pointing_product_name: productName,
      mortar_supplier: supplier,
      buf_pct: Number(buf) || 0,
      mortar_coverage:   mCovNum,
      pointing_coverage: pCovNum,
      cap_mL: Number(capML) || 0,
    });
  };

  return (
    <div
      data-testid={`bedpoint-bar-${section.toLowerCase()}`}
      className="rounded-md border border-amber-200 bg-amber-50/40 px-3 py-2 space-y-2"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700">
          🧱 Bed &amp; Point Materials ({section})
        </span>
        <span className="text-[10px] font-mono-num text-slate-600">
          Bed-and-point: <strong className="text-slate-800">{bedPointM.toFixed(1)} m</strong>
          {pointOnlyM > 0 && (
            <> · Point-only: <strong className="text-slate-800">{pointOnlyM.toFixed(1)} m</strong></>
          )}
        </span>
        <button
          type="button"
          onClick={commit}
          data-testid={`bedpoint-recompute-${section.toLowerCase()}`}
          className="ml-auto text-[10px] px-2 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 font-semibold"
          title="Recompute and commit auto-lines"
        >↻ Recompute</button>
      </div>

      <div className="grid grid-cols-4 gap-2">
        <label className="block">
          <span className="block text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Buffer %</span>
          <input type="number" min={0} max={100} step={1}
            value={buf} onChange={(e) => setBuf(e.target.value)} onBlur={commit}
            data-testid={`bedpoint-buffer-${section.toLowerCase()}`}
            className="w-full text-xs border border-slate-300 rounded px-2 py-1 font-mono-num bg-white" />
        </label>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Mortar m/bag</span>
          <input type="number" min={0.1} step={0.1}
            value={mCov} onChange={(e) => setMCov(e.target.value)} onBlur={commit}
            data-testid={`bedpoint-mortar-cov-${section.toLowerCase()}`}
            className="w-full text-xs border border-slate-300 rounded px-2 py-1 font-mono-num bg-white" />
        </label>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Pointing m/bucket</span>
          <input type="number" min={0.1} step={0.5}
            value={pCov} onChange={(e) => setPCov(e.target.value)} onBlur={commit}
            data-testid={`bedpoint-pointing-cov-${section.toLowerCase()}`}
            className="w-full text-xs border border-slate-300 rounded px-2 py-1 font-mono-num bg-white" />
        </label>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Cap-fill mL</span>
          <input type="number" min={0} step={1}
            value={capML} onChange={(e) => setCapML(e.target.value)} onBlur={commit}
            data-testid={`bedpoint-cap-${section.toLowerCase()}`}
            className="w-full text-xs border border-slate-300 rounded px-2 py-1 font-mono-num bg-white" />
        </label>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <label className="block">
          <span className="block text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Mortar supplier</span>
          <select value={supplier} onChange={(e) => setSupp(e.target.value)} onBlur={commit}
            data-testid={`bedpoint-supplier-${section.toLowerCase()}`}
            className="w-full text-xs border border-slate-300 rounded px-2 py-1 bg-white">
            {mortarSuppliers.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="block col-span-2">
          <span className="block text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Pointing product</span>
          <select value={productName} onChange={(e) => setProd(e.target.value)} onBlur={commit}
            data-testid={`bedpoint-product-${section.toLowerCase()}`}
            className="w-full text-xs border border-slate-300 rounded px-2 py-1 bg-white">
            {pointingOptions.length === 0 && <option value="">⚠ no pointing products in catalogue</option>}
            {pointingOptions.map(r => (
              <option key={r._id || r.type_name} value={r.type_name}>{r.type_name} {r.supplier ? `· ${r.supplier}` : ''}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-3 gap-2 text-[11px] pt-1 border-t border-amber-200">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500">Mortar bags</div>
          <div className="font-mono-num">
            raw <strong>{rawBags.toFixed(2)}</strong>
            {' '}→ order <strong className="text-amber-700">{totalBags}</strong>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500">Pointing buckets</div>
          <div className="font-mono-num">
            raw <strong>{rawBuckets.toFixed(2)}</strong>
            {' '}→ order <strong className="text-amber-700">{totalBuckets}</strong>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500">Plasticizer (info)</div>
          <div className="font-mono-num">
            <strong className="text-slate-700">{plasticizerMl.toFixed(0)} mL</strong> ({plasticizerL.toFixed(2)} L)
          </div>
        </div>
      </div>

      {(savedMortarQty || savedPointingQty) ? (
        <div className="text-[10px] text-emerald-700">
          live · mortar × {savedMortarQty} · pointing × {savedPointingQty}
        </div>
      ) : (
        <div className="text-[10px] text-amber-700">
          Not saved yet — hit ↻ Recompute to commit.
        </div>
      )}
    </div>
  );
}
